/**
 * Place one staged bank line across the open items it settles.
 *
 * The inbox's answer list. The engine already proposes what it is sure of; this
 * is where an operator decides the rest. A line does not have to be spent in
 * one go: each item takes an amount, and whatever is left stays on the line and
 * goes back to the inbox, so a wire larger than any single call no longer has
 * to be refused.
 *
 * Where the engine already worked out which items one payer covers, the basket
 * opens on that answer and the operator confirms it rather than re-deriving it.
 *
 * It takes over the whole module rather than opening over it: the open-items
 * table is as wide as the inbox's own, and an operator comparing the two reads
 * both at full width. The caller carries which line is being matched in the
 * URL, so the browser's back button leaves this screen the way it arrived.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowLeft,
  ArrowLeftRight,
  Check,
  Receipt,
  Search,
  Sparkles,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DealCostFromBankLineForm } from '@/components/bank-incoming/DealCostFromBankLineForm';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAllocateBankIncoming,
  useBankIncomingOpenItems,
  useMarkBankIncomingInternalTransfer,
  useMatchBankIncomingOpenItem,
} from '@/hooks/react-query/useBankIncoming';
import type {
  BankIncomingScope,
  SplitAllocationPayload,
} from '@/services/bankIncomingService';
import {
  BANK_TRANSACTION_TYPE_LABELS,
  MATCH_BLOCKED_REASON_LABELS,
  OPEN_ITEM_KIND_LABELS,
  type BankIncomingAdminLine,
  type MatchBlockedReason,
  type OpenItem,
  type OpenItemKind,
} from '@/types/bank-reconciliation';
import {
  roundToTheCent,
  sameToTheCent,
  shortOfToTheCent,
} from '@/utils/number-operations';
import { formatCurrency, formatDate } from '@/utils/formatters';

const ALL = 'all';
const DEFAULT_PAGE_SIZE = 25;

// What happens to whatever the open items leave over.
type RemainderUse =
  'KEEP_OPEN' | 'HOLD_AS_LP_CREDIT' | 'BOOK_TO_MANAGER_CURRENT_ACCOUNT';

const KIND_OPTIONS = Object.entries(OPEN_ITEM_KIND_LABELS) as [
  OpenItemKind,
  string,
][];

export interface MatchManuallyViewProps {
  line: BankIncomingAdminLine;
  /** Omitted in the admin module; set to the fund when shown inside a fund. */
  scope?: BankIncomingScope;
  /** Leaves this screen and returns to the inbox. */
  onClose: () => void;
  /** Fired once the line is settled, before the screen closes. */
  onMatched?: () => void;
  /** The deal cost form instead of the open items, when it can take the line. */
  opensOn?: MatchScreenForm;
}

export type MatchScreenForm = 'OPEN_ITEMS' | 'DEAL_COST';

export function MatchManuallyView({
  line,
  scope,
  onClose,
  onMatched,
  opensOn = 'OPEN_ITEMS',
}: MatchManuallyViewProps) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<string>(ALL);
  // What the operator has typed, keyed by open item, as the raw input string so
  // a half-typed "1." is not rewritten under the cursor. Null until they touch
  // anything, which is what lets the engine's own answer stand as the default
  // without copying it into state.
  const [edited, setEdited] = useState<Record<string, string> | null>(null);
  const [remainderUse, setRemainderUse] = useState<RemainderUse>('KEEP_OPEN');
  // A deal cost takes the whole line, and only base-currency money paid out.
  const canBookDealCost =
    line.match_status === 'UNMATCHED' &&
    line.amount < 0 &&
    line.is_in_fund_base_currency;
  const [bookingDealCost, setBookingDealCost] = useState(
    opensOn === 'DEAL_COST' && canBookDealCost
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const filters = useMemo(
    () => ({
      ...(search ? { search } : {}),
      ...(kind === ALL ? {} : { kind: kind as OpenItemKind }),
      page,
      page_size: pageSize,
    }),
    [search, kind, page, pageSize]
  );

  const { data, isLoading } = useBankIncomingOpenItems(line.id, filters, scope);
  const allocate = useAllocateBankIncoming(scope);
  const matchWhole = useMatchBankIncomingOpenItem(scope);
  const markInternalTransfer = useMarkBankIncomingInternalTransfer(scope);

  const items = data?.items;
  const total = data?.total_count ?? 0;

  // The engine's own answer, derived rather than seeded into state: where it
  // named the items one payer covers, the basket opens on them. Matched up by
  // id, so it only reaches items actually on this page.
  const suggested = useMemo(() => {
    const seeded: Record<string, string> = {};
    (items ?? []).forEach((item) => {
      const suggestion = line.suggested_allocations.find(
        (candidate) =>
          item.id ===
          (candidate.drawdown_investor_id ?? candidate.fund_transaction_id)
      );
      if (suggestion) seeded[item.id] = String(suggestion.amount);
    });
    return seeded;
  }, [items, line.suggested_allocations]);

  const placed = edited ?? suggested;

  // Derived, not stored: an amount typed against an item hidden by a later
  // filter must not be submitted behind the operator's back.
  const basket = useMemo(
    () =>
      (items ?? [])
        .filter((item) => placed[item.id] !== undefined)
        .map((item) => ({
          item,
          amount: roundToTheCent(Number(placed[item.id]) || 0),
        }))
        .filter(({ amount }) => amount > 0),
    [items, placed]
  );

  const totalPlaced = roundToTheCent(
    basket.reduce((sum, entry) => sum + entry.amount, 0)
  );
  const left = roundToTheCent(line.unapplied_amount - totalPlaced);
  const overPlaced = basket.some(
    ({ item, amount }) => amount > item.outstanding
  );
  const overLine = shortOfToTheCent(line.unapplied_amount, totalPlaced);
  // Only money coming in can be held: a payment out that nobody can attribute
  // is a different problem, and holding it would say the fund owes the payer
  // money it has just sent them.
  const canHold =
    Boolean(line.payer_investor_id) && line.amount > 0 && left > 0;
  const holding = canHold && remainderUse === 'HOLD_AS_LP_CREDIT';
  const canBookToManager = Boolean(line.fund_manager_entity_id) && left > 0;
  const bookingToManager =
    canBookToManager && remainderUse === 'BOOK_TO_MANAGER_CURRENT_ACCOUNT';
  const remainderIsSettled = holding || bookingToManager;

  const portfolio = basket.find(
    ({ item }) => item.captable_transaction_id !== null
  );
  const portfolioIsShared =
    portfolio !== undefined && (basket.length > 1 || remainderIsSettled);
  const canSubmit =
    (basket.length > 0 || remainderIsSettled) &&
    !overPlaced &&
    !overLine &&
    !portfolioIsShared;
  const isSubmitting =
    allocate.isPending ||
    matchWhole.isPending ||
    markInternalTransfer.isPending;
  // Only a whole line can be an internal transfer.
  const canMarkInternalTransfer = line.match_status === 'UNMATCHED';
  // Both kinds are listed only while still unplaced, so an operator looking
  // for one that is already matched would otherwise be left guessing.
  const showsPlaceableOnly =
    kind === ALL || kind === 'BANK_LINE' || kind === 'UNAPPLIED_CASH';
  const confirmInternalTransfer = async () => {
    await markInternalTransfer.mutateAsync(line.id);
    onMatched?.();
    onClose();
  };

  const confirm = async () => {
    if (!canSubmit) return;
    if (portfolio?.item.captable_transaction_id) {
      await matchWhole.mutateAsync({
        incomingId: line.id,
        target: {
          captable_transaction_id: portfolio.item.captable_transaction_id,
        },
      });
      onMatched?.();
      onClose();
      return;
    }
    await allocate.mutateAsync({
      incomingId: line.id,
      allocations: [
        ...basket.flatMap(({ item, amount }): SplitAllocationPayload[] => {
          if (item.drawdown_investor_id) {
            return [
              { amount, drawdown_investor_id: item.drawdown_investor_id },
            ];
          }
          if (item.counter_line_id) {
            return [{ amount, counter_line_id: item.counter_line_id }];
          }
          if (item.unapplied_investor_id) {
            return [
              { amount, unapplied_investor_id: item.unapplied_investor_id },
            ];
          }
          if (item.returned_fund_transaction_id) {
            return [
              {
                amount,
                returned_fund_transaction_id: item.returned_fund_transaction_id,
              },
            ];
          }
          return item.fund_transaction_id
            ? [{ amount, fund_transaction_id: item.fund_transaction_id }]
            : [];
        }),
        ...(holding && line.payer_investor_id
          ? [{ amount: left, unapplied_investor_id: line.payer_investor_id }]
          : []),
        ...(bookingToManager && line.fund_manager_entity_id
          ? [
              {
                amount: left,
                fund_manager_entity_id: line.fund_manager_entity_id,
              },
            ]
          : []),
      ],
    });
    onMatched?.();
    onClose();
  };

  const toggle = (item: OpenItem) =>
    setEdited((current) => {
      const next = { ...(current ?? suggested) };
      if (next[item.id] !== undefined) {
        delete next[item.id];
        return next;
      }
      // The obvious answer, so most rows need no typing: as much as this item
      // can take, capped by what is left of the line.
      const remaining = roundToTheCent(line.unapplied_amount - totalPlaced);
      next[item.id] = String(
        roundToTheCent(Math.min(item.outstanding, Math.max(remaining, 0)))
      );
      return next;
    });

  const allocateLabel =
    sameToTheCent(left, 0) || remainderIsSettled
      ? 'Allocate'
      : 'Allocate and keep the rest open';
  const confirmLabel = portfolio ? 'Confirm' : allocateLabel;

  return (
    <div className="space-y-4">
      <Button
        variant="link"
        className="h-auto gap-1.5 p-0 text-muted-foreground"
        onClick={onClose}
      >
        <ArrowLeft className="h-4 w-4" />
        Incoming Transactions
      </Button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Allocate Transaction
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose the open items this bank transaction settles, and how much of
            it each one takes. You can place part of it and leave the rest for
            later.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={confirmInternalTransfer}
            disabled={!canMarkInternalTransfer || isSubmitting}
            className="gap-1.5"
          >
            <ArrowLeftRight className="h-4 w-4" />
            Mark as internal transfer
          </Button>
          <Button
            variant="outline"
            onClick={() => setBookingDealCost(true)}
            disabled={!canBookDealCost || isSubmitting || bookingDealCost}
            className="gap-1.5"
          >
            <Receipt className="h-4 w-4" />
            Mark as deal cost
          </Button>
          <Button
            onClick={confirm}
            disabled={!canSubmit || isSubmitting || bookingDealCost}
            className="gap-1.5"
          >
            <Check className="h-4 w-4" />
            {confirmLabel}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[360px_minmax(0,1fr)] items-start">
        <BankLineCard line={line} placed={totalPlaced} left={left} />

        {bookingDealCost ? (
          <DealCostFromBankLineForm
            line={line}
            onCancel={() => setBookingDealCost(false)}
            onBooked={() => {
              onMatched?.();
              onClose();
            }}
          />
        ) : (
          <Card className="min-w-0">
            <CardContent className="pt-6 space-y-4">
              <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Reference, investor, item..."
                    className="pl-9"
                  />
                </div>

                <Select
                  value={kind}
                  onValueChange={(value) => {
                    setKind(value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[190px]">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All categories</SelectItem>
                    {KIND_OPTIONS.map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {showsPlaceableOnly && (
                <p className="text-xs text-muted-foreground">
                  Only what is still unplaced can be picked. A bank line already
                  matched, or an LP payment already applied to a call, has to be
                  unlinked before it can be paired or refunded.
                </p>
              )}

              {isLoading || !items || items.length === 0 ? (
                <OpenItemsPlaceholder isLoading={isLoading} />
              ) : (
                <div className="rounded-lg border border-border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-11" />
                        <TableHead>Open item</TableHead>
                        <TableHead className="w-[150px]">Category</TableHead>
                        <TableHead className="w-[110px]">Due</TableHead>
                        <TableHead className="w-[140px] text-right">
                          Outstanding
                        </TableHead>
                        <TableHead className="w-[150px] text-right">
                          Allocate
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => (
                        <OpenItemRow
                          key={item.id}
                          item={item}
                          amount={placed[item.id]}
                          onToggle={() => item.can_match && toggle(item)}
                          onAmountChange={(value) =>
                            setEdited((current) => ({
                              ...(current ?? suggested),
                              [item.id]: value,
                            }))
                          }
                        />
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {canHold && (
                <label className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={holding}
                    onChange={(event) =>
                      setRemainderUse(
                        event.target.checked ? 'HOLD_AS_LP_CREDIT' : 'KEEP_OPEN'
                      )
                    }
                  />
                  <span>
                    <span className="font-medium text-foreground">
                      Hold the remaining{' '}
                      {formatCurrency(left, {
                        currency: line.currency ?? undefined,
                      })}{' '}
                      as {line.payer_investor_name}&apos;s credit
                    </span>
                    <span className="block text-muted-foreground">
                      The fund keeps the money under their name until it settles
                      a call or is refunded. It is not a contribution.
                    </span>
                  </span>
                </label>
              )}

              {canBookToManager && (
                <label className="flex items-start gap-2 rounded-lg border border-border p-3 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={bookingToManager}
                    onChange={(event) =>
                      setRemainderUse(
                        event.target.checked
                          ? 'BOOK_TO_MANAGER_CURRENT_ACCOUNT'
                          : 'KEEP_OPEN'
                      )
                    }
                  />
                  <span>
                    <span className="font-medium text-foreground">
                      Book the remaining{' '}
                      {formatCurrency(left, {
                        currency: line.currency ?? undefined,
                      })}{' '}
                      to the current account with {line.fund_manager_name}
                    </span>
                    <span className="block text-muted-foreground">
                      {line.amount > 0
                        ? 'Cash the manager fronted the fund. The fund owes it back.'
                        : 'The fund repays cash the manager fronted.'}
                    </span>
                  </span>
                </label>
              )}

              {portfolioIsShared && (
                <p className="text-sm text-red-600">
                  A portfolio transaction is confirmed by the whole bank
                  transaction, so it cannot be combined with anything else.
                </p>
              )}

              {overLine && (
                <p className="text-sm text-red-600">
                  That is{' '}
                  {formatCurrency(totalPlaced - line.unapplied_amount, {
                    currency: line.currency ?? undefined,
                  })}{' '}
                  more than this transaction still holds.
                </p>
              )}

              {total > pageSize && (
                <Pagination
                  current={page}
                  total={total}
                  pageSize={pageSize}
                  onChange={setPage}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                  pageSizeOptions={[25, 50, 100]}
                />
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// What the operator is placing, and what is still to go: kept beside the list
// so neither figure ever scrolls away.
function OpenItemsPlaceholder({ isLoading }: { isLoading: boolean }) {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading open items…</p>;
  }
  return (
    <p className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
      No open item can take this transaction.
    </p>
  );
}

function BankLineCard({
  line,
  placed,
  left,
}: {
  line: BankIncomingAdminLine;
  placed: number;
  left: number;
}) {
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        Bank transaction
      </p>
      <p
        className={`text-2xl font-semibold ${
          line.amount < 0 ? 'text-red-600' : 'text-green-700'
        }`}
      >
        {formatCurrency(line.amount, {
          currency: line.currency ?? undefined,
        })}
      </p>
      <div>
        <p className="text-sm font-medium text-foreground">
          {line.counterparty_name || line.counterparty_reference || ''}
        </p>
        <p className="text-xs text-muted-foreground">
          {BANK_TRANSACTION_TYPE_LABELS[line.bank_transaction_type]}
        </p>
      </div>
      <dl className="space-y-2 text-sm border-y border-border py-3">
        <CardRow
          label="Already allocated"
          value={formatCurrency(line.allocated_amount, {
            currency: line.currency ?? undefined,
          })}
        />
        <CardRow
          label="Placing now"
          value={formatCurrency(placed, {
            currency: line.currency ?? undefined,
          })}
        />
      </dl>
      <div>
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Left to place
        </p>
        <p
          className={`text-xl font-semibold ${
            sameToTheCent(left, 0) ? 'text-green-700' : 'text-amber-600'
          }`}
        >
          {formatCurrency(left, { currency: line.currency ?? undefined })}
        </p>
      </div>
      <dl className="space-y-2 text-sm">
        <CardRow
          label="Value date"
          value={formatDate(line.value_date ?? line.execution_date)}
        />
        <CardRow
          label="Counterparty IBAN"
          value={line.counterparty_reference}
        />
        <CardRow
          label="Reference"
          value={line.end_to_end_id || line.remittance_information}
        />
        <CardRow label="Bank account" value={line.account_name} />
      </dl>
    </div>
  );
}

function CardRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground shrink-0">{label}</dt>
      {/* min-w-0 is what lets this shrink: a flex item's min width is its
          content's, and an IBAN is one unbreakable word, so without it the
          value runs past the edge of the card however wide the card gets. */}
      <dd className="min-w-0 break-words text-right text-foreground">
        {value || ''}
      </dd>
    </div>
  );
}

function OpenItemRow({
  item,
  amount,
  onToggle,
  onAmountChange,
}: {
  item: OpenItem;
  amount: string | undefined;
  onToggle: () => void;
  onAmountChange: (value: string) => void;
}) {
  const chosen = amount !== undefined;
  const tooMuch = chosen && Number(amount) > item.outstanding;
  const selectableClass = `cursor-pointer ${chosen ? 'bg-muted' : ''}`;
  return (
    <TableRow
      onClick={onToggle}
      aria-selected={chosen}
      className={item.can_match ? selectableClass : 'opacity-60'}
    >
      <TableCell>
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={chosen}
          disabled={!item.can_match}
          onChange={onToggle}
          // The row toggles too, so without this a click on the box counts
          // twice and lands back where it started.
          onClick={(event) => event.stopPropagation()}
          aria-label={item.label}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-foreground">{item.label}</span>
          {item.suggested && (
            <Badge variant="outline" className="gap-1 whitespace-nowrap">
              <Sparkles className="h-3 w-3" />
              Suggested
            </Badge>
          )}
          {item.blocked_reason && (
            <span className="text-xs text-muted-foreground">
              {MATCH_BLOCKED_REASON_LABELS[
                item.blocked_reason as MatchBlockedReason
              ] ?? item.blocked_reason}
            </span>
          )}
          {item.held_by_portfolio_company_id &&
            item.held_by_captable_transaction_id && (
              <Link
                to={`/app/portfolio/${item.held_by_portfolio_company_id}/transactions/${item.held_by_captable_transaction_id}`}
                className="text-xs underline"
                onClick={(event) => event.stopPropagation()}
              >
                Open the transaction holding it
              </Link>
            )}
        </div>
        {item.reference && (
          <p className="text-xs text-muted-foreground">{item.reference}</p>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {OPEN_ITEM_KIND_LABELS[item.kind]}
      </TableCell>
      <TableCell className="text-muted-foreground whitespace-nowrap">
        {formatDate(item.due_date)}
      </TableCell>
      <TableCell className="text-right">
        {formatCurrency(item.outstanding, {
          currency: item.currency ?? undefined,
        })}
      </TableCell>
      <TableCell className="text-right">
        {chosen ? (
          <div className="space-y-1">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={amount}
              onChange={(event) => onAmountChange(event.target.value)}
              onClick={(event) => event.stopPropagation()}
              aria-label={`Amount to allocate to ${item.label}`}
              aria-invalid={tooMuch}
              className={`h-8 text-right ${tooMuch ? 'border-red-600' : ''}`}
            />
            {tooMuch && (
              <p className="text-xs text-red-600">
                More than the{' '}
                {formatCurrency(item.outstanding, {
                  currency: item.currency ?? undefined,
                })}{' '}
                outstanding
              </p>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
    </TableRow>
  );
}
